import { AIChatPlugin, useEditorChat, useLastAssistantMessage } from '@alpod-ai-writer/plate-plugins/ai/react';
import { isHotkey,type NodeEntry } from '@udecode/plate';
import {
    useEditorPlugin,
    useHotkeys,
    usePluginOption,
} from '@udecode/plate/react';
import {
    BlockSelectionPlugin,
    // useIsSelecting,
} from '@udecode/plate-selection/react';
import { Loader2Icon, SendHorizonalIcon } from 'lucide-react';
import * as React from 'react';

import { useCredits } from '../../../app/hooks/useCredits.hook';
import { AiFeatureType } from '../../../app/types/credit.types';
import { CreditConfirmationModal } from '../../../components/modals/creditConfirmation/creditConfirmationModal.component';
import { InsufficientCreditsModal } from '../../../components/modals/insufficientCredits/insufficientCreditsModal.component';
import { useModals } from '../../../hooks/useModals.hook';
import { useAiChat } from '../../hooks/useAiChat.hook';
import { AIChatEditor } from './aiChatEditor.component';
import { AIMenuItems } from './aiMenuItems.component';
import { Command, CommandList, InputCommand } from './command.component';
import { Popover, PopoverAnchor, PopoverContent } from './popover.component';

export function AIMenu() {
    const { api, editor } = useEditorPlugin(AIChatPlugin);
    const open = usePluginOption(AIChatPlugin, 'open');
    const mode = usePluginOption(AIChatPlugin, 'mode');
    // const isSelecting = useIsSelecting();
    const [value, setValue] = React.useState('');
    const [input, setInput] = React.useState('');
    const { canUseFeature } = useCredits();
    const modals = useModals();
    const chat = useAiChat();
    const { status, messages, setMessages } = chat;
    const isLoading = status === 'streaming' || status === 'submitted';
    const anchorRef = React.useRef<HTMLElement | null>(null);
    const submittingRef = React.useRef(false);

    const lastAssistantMessage = useLastAssistantMessage();
    const content = lastAssistantMessage ? lastAssistantMessage.parts
        .filter((part: any) => part.type === 'text')
        .map((part: any) => part.type === 'text' ? part.text : '')
        .join('') : '';

    const updateAnchorElement = (el: HTMLElement) => {
        anchorRef.current = el;
    };

    const setOpen = (open: boolean) => {
        if (open) {
            api.aiChat.show(updateAnchorElement);
        } else {
            api.aiChat.hide();
        }
    };

    const show = (anchorElement: HTMLElement) => {
        setMessages([]);
        anchorRef.current = anchorElement;
        setOpen(true);
    };

    useEditorChat({
        chat,
        onOpenBlockSelection: (blocks: NodeEntry[]) => {
            show(editor.api.toDOMNode(blocks.at(-1)![0])!);
        },
        onOpenChange: (open) => {
            if (!open) {
                anchorRef.current = null;
                setInput('');
            }
        },
        onOpenCursor: () => {
            // Fall back to first block when editor has no active selection
            const blockEntry = editor.api.block({ highest: true })
                ?? (editor.children.length > 0 ? [editor.children[0], [0]] as NodeEntry : null);
            if (!blockEntry) return;
            const [ancestor] = blockEntry;
            if (!editor.api.isAt({ end: true }) && !editor.api.isEmpty(ancestor)) {
                editor
                    .getApi(BlockSelectionPlugin)
                    .blockSelection.set((ancestor as any).id as string);
            }
            const domNode = editor.api.toDOMNode(ancestor);
            if (!domNode) return;
            show(domNode);
        },
        onOpenSelection: () => {
            show(editor.api.toDOMNode(editor.api.blocks().at(-1)![0])!);
        },
    });

    const submitPrompt = React.useCallback(() => {
        if (input.length === 0) return;
        const { canUse, requiredCredits, currentBalance } = canUseFeature(AiFeatureType.LANGUAGE_CORRECTION, 1);
        if (!canUse) {
            modals.showModal('insufficient-credits', InsufficientCreditsModal, {
                currentBalance,
                requiredCredits,
                featureName: 'AI asistent',
            });
            return;
        }
        const promptToSend = input;
        setInput('');
        submittingRef.current = true;
        modals.showModal('credit-confirm', CreditConfirmationModal, {
            creditCost: requiredCredits,
            featureName: 'AI asistent',
            onConfirm: () => {
                void api.aiChat.submit({
                    mode: 'chat',
                    prompt: promptToSend,
                });
                // Reset after brief delay — isLoading takes over as the close guard
                setTimeout(() => { submittingRef.current = false; }, 100);
            },
        });
        // Safety fallback: reset if modal is dismissed without confirming
        setTimeout(() => { submittingRef.current = false; }, 30_000);
    }, [api, canUseFeature, input, modals]);

    useHotkeys(
        'meta+j',
        () => {
            api.aiChat.show(updateAnchorElement);
        },
        { enableOnContentEditable: true, enableOnFormTags: true }
    );

    return (
        <Popover open={open} onOpenChange={setOpen} modal={false}>
            <PopoverAnchor virtualRef={anchorRef} />
            <PopoverContent
                className="border-none bg-transparent p-0 shadow-none"
                style={{
                    width: anchorRef.current?.offsetWidth,
                }}
                onFocusOutside={(e) => {
                    if (isLoading || submittingRef.current || document.querySelector('[role="dialog"]')) {
                        e.preventDefault();
                    }
                }}
                onInteractOutside={(e) => {
                    if (isLoading || submittingRef.current || document.querySelector('[role="dialog"]')) {
                        e.preventDefault();
                    }
                }}
                onEscapeKeyDown={(e: KeyboardEvent) => {
                    e.preventDefault();
                    if (isLoading) {
                        api.aiChat.stop();
                    } else {
                        api.aiChat.hide();
                    }
                }}
                align="center"
                side="bottom"
            >
                <Command
                    className="w-full rounded-lg border shadow-md overflow-y-auto max-h-[var(--radix-popover-content-available-height,_80vh)]"
                    value={value}
                    onValueChange={setValue}
                >
                    {mode === 'chat' && content && (
                        <AIChatEditor content={content} />
                    )}
                    {isLoading ? (
                        <div className="flex grow items-center gap-2 p-2 text-sm text-gray-500 select-none">
                            <Loader2Icon className="size-4 animate-spin" />
                            {messages.length > 1 ? 'Upravuji...' : 'Přemýšlím...'}
                        </div>
                    ) : (
                        <div className="flex items-center">
                            <InputCommand
                                variant="ghost"
                                className="flex-1"
                                value={input}
                                onKeyDown={(e: React.KeyboardEvent) => {
                                    if (isHotkey('backspace')(e) && input.length === 0) {
                                        e.preventDefault();
                                        api.aiChat.hide();
                                    }
                                    if (isHotkey('enter')(e) && !e.shiftKey && input.length > 0) {
                                        e.preventDefault();
                                        submitPrompt();
                                    }
                                }}
                                onValueChange={setInput}
                                placeholder="Co má AI udělat?"
                                data-plate-focus
                                autoFocus
                            />
                            {input.length > 0 && (
                                <button
                                    className="mr-2 flex items-center justify-center rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                                    onMouseDown={(e) => { e.preventDefault(); submitPrompt(); }}
                                    title="Odeslat"
                                >
                                    <SendHorizonalIcon className="size-4" />
                                </button>
                            )}
                        </div>
                    )}
                    {!isLoading && (
                        <CommandList>
                            <AIMenuItems setValue={setValue} />
                        </CommandList>
                    )}
                </Command>
            </PopoverContent>
        </Popover>
    );
}
